import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const SessionMap = ({ sessions }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Group sessions by location
  const locationGroups = sessions.reduce((acc, session) => {
    const key = `${session.coordinates.lat},${session.coordinates.lng}`;
    if (!acc[key]) {
      acc[key] = {
        location: session.location,
        coordinates: session.coordinates,
        sessions: []
      };
    }
    acc[key].sessions.push(session);
    return acc;
  }, {});

  const locations = Object.values(locationGroups);

  const getLocationRiskLevel = (sessions) => {
    const riskLevels = sessions.map(s => s.riskLevel);
    if (riskLevels.includes('high')) return 'high';
    if (riskLevels.includes('medium')) return 'medium';
    return 'low';
  };

  const getRiskColor = (riskLevel) => {
    const colors = {
      low: 'bg-success',
      medium: 'bg-warning',
      high: 'bg-error'
    };
    return colors[riskLevel] || colors.low;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-text-primary">Географическое распределение сессий</h3>
        <div className="flex items-center gap-4 text-sm text-text-secondary">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <span>Низкий риск</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-warning rounded-full"></div>
            <span>Средний риск</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-error rounded-full"></div>
            <span>Высокий риск</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2">
          <div className="bg-secondary-100 rounded-lg h-96 relative overflow-hidden">
            {/* Mock map with location markers */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-green-100 flex items-center justify-center">
              <div className="text-center">
                <Icon name="Map" size={48} className="text-secondary-400 mx-auto mb-4" />
                <p className="text-text-secondary">Интерактивная карта сессий</p>
                <p className="text-sm text-text-secondary mt-2">
                  Здесь будет отображаться карта с маркерами активных сессий
                </p>
              </div>
            </div>

            {/* Location markers overlay */}
            <div className="absolute inset-0">
              {locations.map((location, index) => {
                const riskLevel = getLocationRiskLevel(location.sessions);
                const left = 20 + (index * 15) % 60; // Mock positioning
                const top = 30 + (index * 10) % 40;

                return (
                  <button
                    key={`${location.coordinates.lat},${location.coordinates.lng}`}
                    onClick={() => setSelectedLocation(location)}
                    className={`
                      absolute w-6 h-6 rounded-full border-2 border-white shadow-lg
                      hover:scale-110 transition-transform duration-150 cursor-pointer
                      ${getRiskColor(riskLevel)}
                    `}
                    style={{ left: `${left}%`, top: `${top}%` }}
                    title={`${location.location} - ${location.sessions.length} сессий`}
                  >
                    <span className="text-xs font-bold text-white">
                      {location.sessions.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Location Details */}
        <div className="space-y-4">
          <h4 className="font-medium text-text-primary">Детали по местоположениям</h4>
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {locations.map((location, index) => {
              const riskLevel = getLocationRiskLevel(location.sessions);
              const isSelected = selectedLocation?.location === location.location;

              return (
                <button
                  key={index}
                  onClick={() => setSelectedLocation(location)}
                  className={`
                    w-full text-left p-3 rounded-lg border transition-all duration-150 hover-lift
                    ${isSelected 
                      ? 'border-primary bg-primary-50' :'border-secondary-200 bg-surface hover:border-secondary-300'
                    }
                  `}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={16} className="text-secondary-500" />
                      <span className="font-medium text-text-primary">
                        {location.location}
                      </span>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${getRiskColor(riskLevel)}`}></div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">
                      {location.sessions.length} активных сессий
                    </span>
                    <span className="text-text-secondary">
                      {location.coordinates.lat.toFixed(4)}, {location.coordinates.lng.toFixed(4)}
                    </span>
                  </div>

                  {isSelected && (
                    <div className="mt-3 pt-3 border-t border-secondary-200">
                      <div className="space-y-2">
                        {location.sessions.map(session => (
                          <div key={session.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                <span className="text-xs font-medium text-white">
                                  {session.userName.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <span className="text-sm text-text-primary">{session.userName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon 
                                name={session.deviceType === 'Desktop' ? 'Monitor' : 
                                      session.deviceType === 'Mobile' ? 'Smartphone' : 'Tablet'} 
                                size={14} 
                                className="text-secondary-500" 
                              />
                              <span className={`
                                w-2 h-2 rounded-full
                                ${session.status === 'active' ? 'bg-success' :
                                  session.status === 'idle' ? 'bg-warning' :
                                  session.status === 'suspicious' ? 'bg-error' : 'bg-secondary-400'}
                              `}></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Statistics */}
          <div className="bg-secondary-50 rounded-lg p-4">
            <h5 className="font-medium text-text-primary mb-3">Статистика по регионам</h5>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Всего местоположений:</span>
                <span className="font-medium text-text-primary">{locations.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Активные сессии:</span>
                <span className="font-medium text-success">
                  {sessions.filter(s => s.status === 'active').length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Подозрительные:</span>
                <span className="font-medium text-error">
                  {sessions.filter(s => s.status === 'suspicious').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionMap;